import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import ProcessList from './ProcessList';
import SystemInfo from './SystemInfo';
import ServerManager from './ServerManager';
import { useSocket } from '../contexts/SocketContext';
import { apiService } from '../services/apiService';

const Dashboard: React.FC = () => {
  const { monitoringData, systemData, isConnected } = useSocket();
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'local' | 'servers'>('local');
  const [newProcess, setNewProcess] = useState({
    script: '',
    name: '',
    instances: 1,
    watch: false,
    autorestart: true,
  });

  useEffect(() => {
    if (monitoringData?.processes) {
      setProcesses(monitoringData.processes);
    }
  }, [monitoringData]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStartProcess = async () => {
    try {
      await apiService.startProcess(newProcess);
      setStartDialogOpen(false);
      setNewProcess({
        script: '',
        name: '',
        instances: 1,
        watch: false,
        autorestart: true,
      });
    } catch (error) {
      console.error('Uygulama başlatma hatası:', error);
    }
  };

  const handleProcessAction = async (action: string, processId: string) => {
    try {
      switch (action) {
        case 'restart':
          await apiService.restartProcess(processId);
          break;
        case 'stop':
          await apiService.stopProcess(processId);
          break;
        case 'delete':
          await apiService.deleteProcess(processId);
          break;
      }
    } catch (error) {
      console.error('İşlem hatası:', error);
    }
  };

  return (
    <Box>
      <AppBar position="static" sx={{ backgroundColor: 'background.paper' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
            PM2 Web Monitor
          </Typography>
          <Chip
            label={isConnected ? 'Bağlı' : 'Bağlantı Yok'}
            color={isConnected ? 'success' : 'error'}
            size="small"
            sx={{ mr: 2 }}
          />
          <IconButton color="inherit" onClick={handleMenuOpen}>
            <SettingsIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => setStartDialogOpen(true)}>
              <AddIcon sx={{ mr: 1 }} />
              Yeni Uygulama Başlat
            </MenuItem>
            <MenuItem onClick={() => window.location.reload()}>
              <RefreshIcon sx={{ mr: 1 }} />
              Yenile
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label="Yerel Sunucu" value="local" />
            <Tab label="Uzak Sunucular" value="servers" />
          </Tabs>
        </Paper>

        {activeTab === 'local' && (
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Sistem Bilgileri */}
            <Box sx={{ flex: { xs: 1, md: '0 0 300px' } }}>
              <SystemInfo systemData={systemData} />
            </Box>

            {/* Ana İçerik */}
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Çalışan Uygulamalar ({processes.length})
                </Typography>
                <ProcessList
                  processes={processes}
                  onProcessAction={handleProcessAction}
                  selectedProcess={selectedProcess}
                  onSelectProcess={setSelectedProcess}
                />
              </Paper>
            </Box>
          </Box>
        )}

        {activeTab === 'servers' && (
          <ServerManager />
        )}
      </Container>

      {/* Yeni Uygulama Başlatma Dialogu */}
      <Dialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Uygulama Başlat</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Script Dosyası"
            fullWidth
            variant="outlined"
            value={newProcess.script}
            onChange={(e) => setNewProcess({ ...newProcess, script: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Uygulama Adı"
            fullWidth
            variant="outlined"
            value={newProcess.name}
            onChange={(e) => setNewProcess({ ...newProcess, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Instance Sayısı</InputLabel>
            <Select
              value={newProcess.instances}
              onChange={(e) => setNewProcess({ ...newProcess, instances: Number(e.target.value) })}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={0}>Max (CPU Sayısı)</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={newProcess.watch}
                onChange={(e) => setNewProcess({ ...newProcess, watch: e.target.checked })}
              />
            }
            label="Dosya Değişikliklerini İzle"
          />
          <FormControlLabel
            control={
              <Switch
                checked={newProcess.autorestart}
                onChange={(e) => setNewProcess({ ...newProcess, autorestart: e.target.checked })}
              />
            }
            label="Otomatik Yeniden Başlat"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)}>İptal</Button>
          <Button onClick={handleStartProcess} variant="contained">
            Başlat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
